import type { ReactNode } from 'react'
import { NavLink, useParams } from 'react-router'
import { CloudOff } from 'lucide-react'
import { cn } from '@/lib/utils'
import { paths } from '@/routes/paths'
import { PageHeader } from '@/components/composites/states'
import type { DealStatus, Level, Stage } from './api'

export const STAGE_LABEL: Record<Stage, string> = {
  demo_booked: 'Demo booked',
  demo_done: 'Demo done',
  pilot_agreed: 'Pilot agreed',
  pilot_running: 'Pilot running',
  pilot_review: 'Pilot review',
  contract: 'Contract',
  won: 'Won',
  lost: 'Lost',
}

/** The three swimlanes the proposal draws the stages in. */
export const STAGE_GROUP: Record<Stage, 'Demo' | 'Pilot' | 'Close'> = {
  demo_booked: 'Demo',
  demo_done: 'Demo',
  pilot_agreed: 'Pilot',
  pilot_running: 'Pilot',
  pilot_review: 'Pilot',
  contract: 'Close',
  won: 'Close',
  lost: 'Close',
}

export const LEVEL_DOT: Record<Level, string> = { green: 'bg-success', yellow: 'bg-warning', red: 'bg-danger' }
export const LEVEL_SOFT: Record<Level, string> = { green: 'bg-success-soft text-success', yellow: 'bg-warning-soft text-warning', red: 'bg-danger-soft text-danger' }
export const LEVEL_LABEL: Record<Level, string> = { green: 'On track', yellow: 'Needs a look', red: 'Needs action' }

export const STATUS_LABEL: Record<DealStatus, string> = { on_track: 'On track', at_risk: 'At risk', blocked: 'Blocked' }
export const STATUS_STYLE: Record<DealStatus, string> = { on_track: 'bg-success-soft text-success', at_risk: 'bg-warning-soft text-warning', blocked: 'bg-danger-soft text-danger' }

export const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

export function money(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—'
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`
  if (n >= 10_000) return `$${Math.round(n / 1000)}k`
  return `$${Math.round(n).toLocaleString('en-US')}`
}

export function shortDay(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export function daysAgo(iso: string | null | undefined): string {
  if (!iso) return 'never'
  const n = Math.floor((Date.now() - Date.parse(iso)) / 86_400_000)
  return n <= 0 ? 'today' : n === 1 ? 'yesterday' : `${n} days ago`
}

/** YYYY-MM-DD for a date input, from whatever the API holds. */
export const dateInput = (iso: string | null | undefined) => (iso ? iso.slice(0, 10) : '')

export function Dot({ level, className }: { level: Level; className?: string }) {
  return <span className={cn('inline-block size-2 shrink-0 rounded-full', LEVEL_DOT[level], className)} aria-label={LEVEL_LABEL[level]} />
}

export function NotInGhl({ className }: { className?: string }) {
  return (
    <span title="Kept in Tapwork. Writing to GoHighLevel is off on this environment." className={cn('inline-flex items-center gap-1 rounded-full bg-muted px-1.5 py-0.5 text-tiny text-text-secondary', className)}>
      <CloudOff className="size-3" />Not in GHL
    </span>
  )
}

/** The pipeline's pages share one header and a row of tabs. */
export function PipelineFrame({ title, description, actions, children }: { title: string; description: string; actions?: ReactNode; children: ReactNode }) {
  const { workspaceSlug = '' } = useParams()
  const tabs = [
    [paths.pipeline(workspaceSlug), 'Board'],
    [paths.pipelineDashboard(workspaceSlug), 'Dashboard'],
    [paths.pipelineCheckin(workspaceSlug), 'Check-in'],
    [paths.pipelineReport(workspaceSlug), 'Monday report'],
    [paths.pipelineSettings(workspaceSlug), 'Settings'],
  ] as const
  return (
    <div>
      <PageHeader kicker="TapCRM" title={title} description={description} actions={actions} badge={<span className="rounded-full bg-info-soft px-2 py-0.5 text-tiny font-semibold text-info">Staging preview</span>} />
      <nav className="-mt-2 mb-4 flex flex-wrap gap-1 border-b border-border-soft" aria-label="Pipeline">
        {tabs.map(([to, label]) => (
          <NavLink key={to} to={to} end className={({ isActive }) => cn('-mb-px border-b-2 px-3 py-2 text-sm', isActive ? 'border-brand font-medium text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground')}>
            {label}
          </NavLink>
        ))}
      </nav>
      {children}
    </div>
  )
}

export function Stat({ label, value, tone, hint }: { label: string; value: ReactNode; tone?: string; hint?: string }) {
  return (
    <div className="rounded-xl border border-border bg-card px-4 py-3 shadow-sm" title={hint}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={cn('num mt-0.5 text-lg font-semibold', tone)}>{value}</div>
    </div>
  )
}
